import { apiClient } from './client';
import { unwrapList } from './unwrap';
import type { PagedResult, SubmissionImage } from './types';

export const imagesApi = {
  /**
   * Multipart upload of the original image file for an ALREADY-CREATED
   * submission. Matches POST /api/v1/images/upload
   * (ImagesController.Upload), which requires a real, non-nullable
   * `submissionId` - so the submission must exist before this is called.
   * Returns the raw ImageDto (file metadata + processing `status`), not a
   * resolved URL: thumbnail/watermark generation happens later in
   * Aperture.Workers.
   */
  upload: (file: File, opts: { submissionId: string; onProgress?: (pct: number) => void }) => {
    const form = new FormData();
    form.append('file', file);
    form.append('submissionId', opts.submissionId);

    return apiClient
      .post<SubmissionImage>('/images/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (opts.onProgress && evt.total) {
            opts.onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      })
      .then((r) => r.data);
  },

  /** GET /images/{id} - current file metadata + processing status/lifecycle stage. */
  get: (id: string) => apiClient.get<SubmissionImage>(`/images/${id}`).then((r) => r.data),

  /** GET /images?submissionId= - all images uploaded for a submission. */
  listBySubmission: (submissionId: string) =>
    apiClient
      .get('/images', { params: { submissionId } })
      .then((r) => unwrapList<SubmissionImage>(r.data)) as Promise<PagedResult<SubmissionImage>>,

  /**
   * DELETE /images/{id} - competitor-owner only, and only while their
   * submission is still Draft or Submitted (ImagesService.DeleteAsync
   * mirrors Submission.UpdateDetails' own edit-window rule).
   */
  remove: (id: string) => apiClient.delete(`/images/${id}`).then(() => undefined),
};
